import React from "react";
import { ArrowRight, BadgeCheck, LineChart, Sparkles, Users } from "lucide-react";

const HeroSection = ({ onGetStarted }) => {
  return (
    <section className="px-4 pb-14 pt-12 sm:pt-16 md:pt-20 relative overflow-hidden" role="banner">
      <div className="max-w-7xl mx-auto hero-surface rounded-[2rem] md:rounded-[2.5rem] p-6 sm:p-10 lg:p-14 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute -bottom-16 -left-12 h-40 w-40 rounded-full bg-cyan-100/50 blur-3xl" />
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] items-center relative z-10">
          <div>
            <div className="section-kicker mb-5">
              <Sparkles className="h-4 w-4" />
              Modern creator growth engine
            </div>
            <header>
              <h1 className="section-heading max-w-3xl">
                A clearer way for brands and creators to <span className="text-gradient-brand">launch campaigns that convert.</span>
              </h1>
              <p className="mt-5 section-copy max-w-2xl">
                Discover high-fit creators, launch campaigns faster, and manage every conversation in one polished workspace designed for modern creator marketing.
              </p>
            </header>

            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-600">
              <span className="stat-chip"><BadgeCheck className="h-4 w-4 text-collaborb-band4" /> Faster campaign discovery</span>
              <span className="stat-chip"><Users className="h-4 w-4 text-collaborb-band4" /> Better creator-brand matching</span>
              <span className="stat-chip"><LineChart className="h-4 w-4 text-collaborb-band4" /> Clearer workflow visibility</span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={onGetStarted}
                className="primary-button"
                aria-label="Get started with CollabOrb creator marketing platform"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#how-it-works" className="secondary-button">
                Explore the workflow
              </a>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="metric-card">
                <p className="text-3xl font-bold text-collab-inner">2x</p>
                <p className="mt-1 text-sm text-slate-500">Stronger first-impression clarity</p>
              </div>
              <div className="metric-card">
                <p className="text-3xl font-bold text-collab-inner">All-in-one</p>
                <p className="mt-1 text-sm text-slate-500">Discovery, bidding, profile, and account flows</p>
              </div>
              <div className="metric-card">
                <p className="text-3xl font-bold text-collab-inner">Role-based</p>
                <p className="mt-1 text-sm text-slate-500">Separate experiences for brands, creators, and admin</p>
              </div>
            </div>
          </div>

          <div className="soft-grid rounded-[2rem] border border-slate-200/80 bg-collaborb-inner p-6 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-sky-200">Campaign workspace</p>
                <h2 className="mt-2 text-2xl font-semibold">Built for confident decisions</h2>
              </div>
              <img src="/collaborb-logo.png" alt="" aria-hidden="true" className="h-12 w-12 rounded-2xl bg-white/10 p-2" />
            </div>

            <div className="mt-6 space-y-4">
              <div className="rounded-2xl bg-white/8 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-300">Campaign health</p>
                    <p className="mt-1 text-2xl font-semibold">High-fit matches</p>
                  </div>
                  <span className="rounded-full bg-collab-accent-soft px-3 py-1 text-xs font-semibold text-sky-200">Live</span>
                </div>
                <div className="mt-4 h-2 rounded-full bg-white/10">
                  <div className="h-2 w-4/5 rounded-full bg-gradient-to-r from-sky-400 to-cyan-300" />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/8 p-4">
                  <p className="text-sm text-slate-300">Creator discovery</p>
                  <p className="mt-2 text-xl font-semibold">Segmented by niche, city, and platform</p>
                </div>
                <div className="rounded-2xl bg-white/8 p-4">
                  <p className="text-sm text-slate-300">Workflow clarity</p>
                  <p className="mt-2 text-xl font-semibold">Bids, deadlines, and approvals in one flow</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
